import React, { useEffect, useState } from "react";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { Card, Typography, Row, Col, Button, Upload, Modal, message, Steps, Tooltip } from "antd";
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  InboxOutlined,
  DownloadOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTender } from "../../contexts/TenderContext";
import { useAuth } from "../../hooks/useAuth";
import { useItemLibrary } from "../../hooks/useItemLibrary";
import { useChangeDetectionContext } from "../../contexts/ChangeDetectionContext";
import MainLayout from "../layout/MainLayout";
import { COLORS } from "../../utils/constants";
import { billOfQuantitiesApi } from "../../api/billOfQuantities";
import { saveTenderToStorage } from "../../utils/storage";
import { forceSaveBothBoqEndpoints } from "../../utils/boqVersionUtils";
import { saveBothWallsBuildUp } from "../../utils/wallsBuildUpSave";
import { importGroundworksTakeoff } from "../../utils/groundworksTakeoffImport";
import { importRoadsAndSewersTakeoff } from "../../utils/roadsAndSewersTakeoffImport";
import { validateGroundworksTakeoff } from "../../utils/groundworksTakeoffValidation";
import { validateRoadsAndSewersTakeoff } from "../../utils/roadsAndSewersTakeoffValidation";
import type { TenderState } from "../../types/tender.types";
import { Layers3, Route } from "lucide-react";

const { Title, Text } = Typography;

// A take-off upload goes through two visible stages before the Dragger goes
// back to idle: "validating" (column-map check against the reference
// template) then "importing" (actually parsing/merging the data). Kept as
// one enum per job type rather than two booleans so the two stages can never
// both be true at once. "error" is a distinct phase (not folded back into
// "idle" immediately) so the stepper below can hold a visible error state on
// the Validate step for as long as the validation-failure Modal stays open.
type ImportPhase = "idle" | "validating" | "importing" | "error";

const IMPORT_STEP_ITEMS = [
  { title: "Upload" },
  { title: "Validate" },
  { title: "Import" },
];

// Drives the per-job-type <Steps>: null means "don't render the stepper at
// all" (nothing has happened yet for this card), keeping an untouched card
// visually unchanged. Once a phase has been entered at least once (including
// a completed import), the stepper stays visible so the outcome is legible
// without relying on the toast, which fades.
function getImportStepsProps(
  phase: ImportPhase,
  hasImported: boolean,
): { current: number; status: "process" | "finish" | "error" } | null {
  switch (phase) {
    case "validating":
      return { current: 1, status: "process" };
    case "importing":
      return { current: 2, status: "process" };
    case "error":
      return { current: 1, status: "error" };
    case "idle":
      return hasImported ? { current: 2, status: "finish" } : null;
  }
}

const TenderImportTakeoff: React.FC = () => {
  useScrollToTop({ behavior: "smooth", delay: 100 });
  const { state, updateField } = useTender();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Set by VersionHistory's per-job-type "Import Take-off" buttons so this
  // page only shows the one job type that was clicked, even when the tender
  // itself has both job types selected. Absent for every other entry point
  // (e.g. the /tender/* new-tender creation flow via TenderDetails.tsx),
  // which keeps showing every selected job type as before.
  const jobTypeParam = searchParams.get("jobType") as
    | "groundworks"
    | "roadsAndSewers"
    | null;
  const queryClient = useQueryClient();
  const { registerPageChanges } = useChangeDetectionContext();
  const { data: itemLibraryData, refetch: refetchItemLibrary } = useItemLibrary(
    state.region || "",
  );

  const [boqData, setBoqData] = useState<any>(null);
  // Set once the BOQ fetch below has settled (success, failure, or skipped
  // for a missing projectName) - guards the "both totals zero" redirect
  // effect further down from firing on the initial render, before `boqData`
  // has actually had a chance to load.
  const [boqDataChecked, setBoqDataChecked] = useState(false);
  const [groundworksImportPhase, setGroundworksImportPhase] =
    useState<ImportPhase>("idle");
  const [roadsAndSewersImportPhase, setRoadsAndSewersImportPhase] =
    useState<ImportPhase>("idle");
  // Sheet count from the last successful import, per job type - null means
  // "nothing imported yet this session". Drives the success indication below
  // each Dragger, which the toast alone (gone after a few seconds) doesn't
  // leave behind.
  const [importedGroundworksSheets, setImportedGroundworksSheets] = useState<
    number | null
  >(null);
  const [importedRoadsAndSewersSheets, setImportedRoadsAndSewersSheets] =
    useState<number | null>(null);
  const [isDownloadingGroundworks, setIsDownloadingGroundworks] = useState(false);
  const [isDownloadingRoadsAndSewers, setIsDownloadingRoadsAndSewers] = useState(false);
  // Set when a Groundworks import included retaining/geo-tober wall data —
  // read by handleContinue, which is where saveBothWallsBuildUp actually runs
  // (the backend save no longer fires at import time, see handleContinue).
  const [hasWallBuildUpDataToSave, setHasWallBuildUpDataToSave] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);
  const isImporting =
    groundworksImportPhase !== "idle" || roadsAndSewersImportPhase !== "idle";
  // Once any job type has real imported data, "Manual Entry" no longer makes
  // sense as an alternative - hide it rather than leaving it clickable
  // alongside data the user just imported.
  const hasImportedAny =
    importedGroundworksSheets !== null || importedRoadsAndSewersSheets !== null;

  // Fetch BOQ data so re-importing over existing take-off data can prompt for
  // confirmation, same check as JobTypeSelection.tsx's bulk-import modal.
  useEffect(() => {
    const fetchBoqData = async () => {
      if (!state.projectName) {
        setBoqDataChecked(true);
        return;
      }
      try {
        const response = await billOfQuantitiesApi.getByProject(
          state.projectName,
          state.opportunityNo,
        );
        setBoqData(response);
      } catch (error) {
        console.error("Error fetching BOQ data:", error);
      } finally {
        setBoqDataChecked(true);
      }
    };
    fetchBoqData();
  }, [state.projectName]);

  const groundworksTotal: number =
    boqData?.billOfQuantities?.[0]?.groundworks?.totals?.grandTotal || 0;
  const roadsAndSewersTotal: number =
    boqData?.billOfQuantities?.[0]?.roadAndSewers?.totals?.grandTotal || 0;
  const bothJobTypeTotalsZero =
    groundworksTotal === 0 && roadsAndSewersTotal === 0;

  // No tender in context at all (e.g. this URL pasted/bookmarked directly,
  // with no draft in TenderContext/localStorage) - this page has nothing to
  // import into (state.jobTypes is empty, there's no project to fetch a BOQ
  // for), so send the user back to the dashboard first. Both legitimate
  // entry points already guarantee a projectName is set before navigating
  // here: TenderDetails.tsx (the new-tender flow) saves tender info first,
  // and VersionHistory's per-job-type Import Take-off buttons dispatch
  // LOAD_PROJECT before navigating - so this only fires on a direct hit.
  useEffect(() => {
    if (!boqDataChecked || state.projectName) return;
    navigate("/opportunities", { replace: true });
  }, [boqDataChecked, state.projectName, navigate]);

  // Guard for the plain/unscoped entry to this page (no `jobType` param —
  // the new-tender creation flow via TenderDetails.tsx): this page is meant
  // for a brand-new tender with nothing imported yet. If either job type
  // already carries a real total, the tender isn't "fresh" and this page
  // shouldn't be reachable directly, so send the user back to the dashboard
  // instead. Scoped visits from VersionHistory's per-job-type Import
  // Take-off buttons (`jobTypeParam` set) are deliberately exempt — those
  // exist specifically to re-import over an already-in-progress tender's
  // existing data.
  useEffect(() => {
    if (jobTypeParam || !boqDataChecked) return;
    if (!bothJobTypeTotalsZero) {
      navigate("/opportunities", { replace: true });
    }
  }, [jobTypeParam, boqDataChecked, bothJobTypeTotalsZero, navigate]);

  const selectedJobTypes: Array<{
    key: "roadsAndSewers" | "groundworks";
    label: string;
    icon: React.ReactNode;
    accent: string;
  }> = [
    ...(state.jobTypes.groundworks && jobTypeParam !== "roadsAndSewers"
      ? [
          {
            key: "groundworks" as const,
            label: "Groundworks",
            icon: <Layers3 size={16} color="#9333EA" />,
            accent: "#F3E8FF",
          },
        ]
      : []),
    ...(state.jobTypes.roadsAndSewers && jobTypeParam !== "groundworks"
      ? [
          {
            key: "roadsAndSewers" as const,
            label: "Roads & Sewers",
            icon: <Route size={16} color={COLORS.primary} />,
            accent: "#E8F4FD",
          },
        ]
      : []),
  ];

  // Helper function to download the GWKS Take-off Sheet V7.2.3.xlsx file from public folder
  const downloadGroundworksTemplate = async () => {
    const baseUrl = import.meta.env.BASE_URL || "/";
    const url = `${baseUrl}GWKS Take-off Sheet V7.2.3.xlsx`;
    const projectName = state.projectName || "Project";

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download GWKS Take-off Sheet V7.2.3.xlsx`);
    }

    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `${projectName} - GWKS Take-off Sheet V7.2.3.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  };

  // Helper function to download the Roads & Sewers take-off template
  const downloadRoadsAndSewersTemplate = async () => {
    const baseUrl = import.meta.env.BASE_URL || "/";
    const sourceFile = "R&S Take-off sheet.xlsx";
    const url = `${baseUrl}${sourceFile}`;
    const projectName = state.projectName || "Project";
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download ${sourceFile}`);
    }
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `${projectName} - R&S Take-off sheet.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  };

  const handleDownloadTemplate = async (
    jobTypeKey: "groundworks" | "roadsAndSewers",
  ) => {
    const setIsDownloading =
      jobTypeKey === "groundworks"
        ? setIsDownloadingGroundworks
        : setIsDownloadingRoadsAndSewers;
    setIsDownloading(true);
    try {
      if (jobTypeKey === "groundworks") {
        await downloadGroundworksTemplate();
      } else {
        await downloadRoadsAndSewersTemplate();
      }
      message.success("Template downloaded successfully!");
    } catch (error) {
      console.error("Template download error:", error);
      message.error(
        "Failed to download template. Please check your connection and try again.",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleGroundworksImport = async (file: File): Promise<boolean> => {
    const hasExistingData = groundworksTotal > 0;

    const proceed = async () => {
      setGroundworksImportPhase("validating");
      // Clear any previous import's success badge/green border/count up front
      // - a re-import in progress should look like a fresh upload, not a
      // stale "Imported" state sitting alongside the new validate/import
      // steps. Re-set on success below; left cleared on failure/warning.
      setImportedGroundworksSheets(null);
      try {
        const validation = await validateGroundworksTakeoff(file);
        if (!validation.isValid) {
          setGroundworksImportPhase("error");
          const resetPhase = () => setGroundworksImportPhase("idle");
          Modal.error({
            title: validation.isWrongFileType
              ? "Not a valid take-off file"
              : "Take-off file doesn't match the template",
            width: 600,
            // Antd's default Modal.error icon (CloseCircleFilled — a red X in
            // a circle) reads too much like a clickable close button next to
            // the title; it isn't one (this modal only closes via OK/afterClose
            // resetting the phase). Swapped for the same ExclamationCircleFilled
            // Modal.confirm uses, which doesn't carry that "X" shape.
            icon: <ExclamationCircleFilled style={{ color: "#faad14" }} />,
            content: validation.isWrongFileType ? (
              // None of the expected worksheets were found at all — this
              // isn't a Groundworks take-off file with a few things out of
              // sync, it's a different file entirely. The itemized
              // "Missing worksheet" list below is meaningless noise here
              // (every sheet would be on it), so show one plain sentence.
              <Text>
                This file doesn't look like a Groundworks take-off workbook —
                none of its expected worksheets were found. Please upload the
                correct take-off file.
              </Text>
            ) : (
              <div>
                <Text>
                  This file's structure doesn't match the Groundworks
                  take-off template. Re-download the template above and
                  re-enter the data, or check these sheets/columns weren't
                  added, removed, or renamed:
                </Text>
                <ul style={{ maxHeight: 240, overflowY: "auto", marginTop: 12 }}>
                  {validation.errors.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              </div>
            ),
            onOk: resetPhase,
            afterClose: resetPhase,
          });
          return;
        }
        setGroundworksImportPhase("importing");
        const result = await importGroundworksTakeoff(file, state.groundworks);
        if (result.importedSheets === 0) {
          message.warning("No valid data found in the specified worksheets");
          setGroundworksImportPhase("idle");
          return;
        }

        const updatedState = {
          ...state,
          groundworks: result.groundworks,
          isDirty: true,
          lastModified: new Date(),
        };

        saveTenderToStorage(updatedState);

        updateField("groundworks", result.groundworks);

        // The actual backend save (BOQ + wall build-up) is deferred until the
        // user clicks "Continue" below, instead of firing immediately here —
        // importing only stages the data locally (context + localStorage) so
        // a user who re-imports or navigates away without clicking Continue
        // never partially persists to the server. See handleContinue.
        if (result.hasWallBuildUpData) {
          setHasWallBuildUpDataToSave(true);
        }

        result.pageChanges.forEach(({ page, count }) => registerPageChanges(page, true, count));

        setImportedGroundworksSheets(result.importedSheets);
        message.success(
          `Successfully imported data from ${result.importedSheets} worksheet(s)`,
        );
        setGroundworksImportPhase("idle");
      } catch (error) {
        console.error("Groundworks import error:", error);
        message.error(`Import failed: ${(error as Error).message}`);
        setGroundworksImportPhase("idle");
      }
    };

    if (hasExistingData) {
      Modal.confirm({
        title: "Override Existing Data?",
        content:
          "This tender already contains Groundworks data. Re-importing will override existing take-off data. Do you want to continue?",
        okText: "Yes, Override",
        cancelText: "Cancel",
        okButtonProps: { danger: true },
        onOk: proceed,
      });
    } else {
      await proceed();
    }
    return false; // Prevent antd Upload's default upload behavior
  };

  const handleRoadsAndSewersImport = async (file: File): Promise<boolean> => {
    const hasExistingData = roadsAndSewersTotal > 0;

    const proceed = async () => {
      setRoadsAndSewersImportPhase("validating");
      // Clear any previous import's success badge/green border/count up
      // front - a re-import in progress should look like a fresh upload, not
      // a stale "Imported" state sitting alongside the new validate/import
      // steps. Re-set on success below; left cleared on failure/warning.
      setImportedRoadsAndSewersSheets(null);
      try {
        const validation = await validateRoadsAndSewersTakeoff(file);
        if (!validation.isValid) {
          setRoadsAndSewersImportPhase("error");
          const resetPhase = () => setRoadsAndSewersImportPhase("idle");
          Modal.error({
            title: validation.isWrongFileType
              ? "Not a valid take-off file"
              : "Take-off file doesn't match the template",
            width: 600,
            // See the Groundworks validation modal's own comment above — same
            // fix, same reason (the default red X-circle icon reads as a
            // close button, but this modal only closes via OK/afterClose).
            icon: <ExclamationCircleFilled style={{ color: "#faad14" }} />,
            content: validation.isWrongFileType ? (
              // See the Groundworks validation modal's own comment above —
              // none of the expected worksheets were found, so the itemized
              // "Missing worksheet" list would just be every sheet name.
              <Text>
                This file doesn't look like a Roads &amp; Sewers take-off
                workbook — none of its expected worksheets were found. Please
                upload the correct take-off file.
              </Text>
            ) : (
              <div>
                <Text>
                  This file's structure doesn't match the Roads & Sewers
                  take-off template. Re-download the template above and
                  re-enter the data, or check these sheets/columns weren't
                  added, removed, or renamed:
                </Text>
                <ul style={{ maxHeight: 240, overflowY: "auto", marginTop: 12 }}>
                  {validation.errors.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              </div>
            ),
            onOk: resetPhase,
            afterClose: resetPhase,
          });
          return;
        }
        setRoadsAndSewersImportPhase("importing");
        const result = await importRoadsAndSewersTakeoff(file, {
          roads: state.roads,
          foulSewers: state.foulSewers,
          pumpStation: state.pumpStation,
          risingMain: state.risingMain,
          stormSewers: state.stormSewers,
          boxCulverts: state.boxCulverts,
          cellularAttenuation: state.cellularAttenuation,
          concAttenuation: state.concAttenuation,
          pondsSwales: state.pondsSwales,
          weholiteAttenuation: state.weholiteAttenuation,
          preliminaryItems: state.preliminaryItems,
          mainServices: state.mainServices,
          pos: state.pos,
          compound: state.compound,
          substation: state.substation,
          spoilReconciliation: state.spoilReconciliation,
        });

        if (result.importedSheets === 0) {
          message.warning("No valid data found in the specified worksheets");
          setRoadsAndSewersImportPhase("idle");
          return;
        }

        // On a re-import, clear every Roads & Sewers dynamic-bill-summary key
        // (the tab list plus every domain's ad-hoc "extra items" bucket)
        // before writing the freshly-imported data over it. None of these are
        // touched by `importRoadsAndSewersTakeoff`'s own `result.updates` —
        // left alone, a dynamic tab or extra row built against the previous
        // sheet would silently survive a re-import, now pointing at take-off
        // data that no longer matches what it was created from. First clear,
        // then merge the import on top, so a re-import always starts clean.
        const clearedDynamicFields: Partial<TenderState> = hasExistingData
          ? {
              roadsAndSewersDynamicBillSummaryTabs: [],
              prelimsExtraItems: {},
              foulSewersExtraItems: {},
              stormSewersExtraItems: {},
              pumpStationExtraItems: {},
              risingMainExtraItems: {},
              pondsSwalesExtraItems: {},
              cellularAttenuationExtraItems: {},
              weholiteAttenuationExtraItems: {},
              concAttenuationExtraItems: {},
              boxCulvertsExtraItems: {},
              roadsExtraItems: {},
              mainServicesExtraItems: {},
              substationExtraItems: {},
              posExtraItems: {},
              compoundExtraItems: {},
            }
          : {};

        const updatedState = {
          ...state,
          ...clearedDynamicFields,
          ...result.updates,
          isDirty: true,
          lastModified: new Date(),
        };

        saveTenderToStorage(updatedState);

        (Object.entries({ ...clearedDynamicFields, ...result.updates }) as [keyof TenderState, unknown][]).forEach(
          ([key, value]) => updateField(key, value),
        );

        // The actual backend save is deferred until the user clicks
        // "Continue" below — see the matching comment in
        // handleGroundworksImport above.

        result.pageChanges.forEach(({ page, count }) => registerPageChanges(page, true, count));

        setImportedRoadsAndSewersSheets(result.importedSheets);
        message.success(
          `Successfully imported data from ${result.importedSheets} worksheet(s)`,
        );
        setRoadsAndSewersImportPhase("idle");
      } catch (error) {
        console.error("Roads & Sewers import error:", error);
        message.error(`Import failed: ${(error as Error).message}`);
        setRoadsAndSewersImportPhase("idle");
      }
    };

    if (hasExistingData) {
      Modal.confirm({
        title: "Override Existing Data?",
        content:
          "This tender already contains Roads & Sewers data. Re-importing will override existing take-off data. Do you want to continue?",
        okText: "Yes, Override",
        cancelText: "Cancel",
        okButtonProps: { danger: true },
        onOk: proceed,
      });
    } else {
      await proceed();
    }
    return false; // Prevent antd Upload's default upload behavior
  };

  // The single point where imported take-off data is actually persisted to
  // the BOQ API — importing above only stages it into context/localStorage.
  // `state` here already reflects whichever job type(s) were imported, since
  // Continue is only reachable once each import's own updateField calls have
  // landed (the Dragger/Continue button stay disabled while isImporting).
  const handleContinue = async () => {
    setIsContinuing(true);
    try {
      // Fresh fetch for the same reason the import handlers used to fetch it
      // themselves before saving — the cached item library (staleTime: 30min,
      // refetchOnMount: false) can be stale/incomplete relative to a full
      // page reload, which mis-prices combined resource codes.
      const freshItemLibraryData =
        (await refetchItemLibrary()).data ?? itemLibraryData;
      // This page isn't nested under /groundworks or /roads-sewers, so
      // route-based version-prefix detection can't apply — without this, the
      // PUT silently sends a bare numeric version instead of GWKS-N/RS-N.
      // A job-type-scoped visit (via the `jobType` query param, set by
      // VersionHistory's per-job-type Import Take-off buttons) always wins
      // here, since it names exactly the version being written regardless of
      // what else the tender has selected. Otherwise, when both job types
      // are selected, only one version can be addressed per save (same known
      // limitation as JobTypeSelection.tsx's combined import) — Roads &
      // Sewers takes priority, matching the navigation priority below.
      const versionPrefixOverride =
        jobTypeParam === "groundworks"
          ? "GWKS"
          : jobTypeParam === "roadsAndSewers"
            ? "RS"
            : state.jobTypes.roadsAndSewers
              ? "RS"
              : state.jobTypes.groundworks
                ? "GWKS"
                : undefined;
      await forceSaveBothBoqEndpoints(
        state,
        "Data imported",
        undefined,
        user?.name,
        freshItemLibraryData,
        state.opportunityNo,
        versionPrefixOverride,
      );
      queryClient.invalidateQueries({ queryKey: ["boq-versions", state.projectName] });

      if (hasWallBuildUpDataToSave) {
        const ohpPercent = state.globalSettings?.groundworks?.overheadAndProfit ?? 0;
        const prelimsPercent = state.globalSettings?.groundworks?.preliminaries ?? 0;
        await saveBothWallsBuildUp({
          tender: state,
          itemLibraryData: freshItemLibraryData,
          ohpPercent,
          prelimsPercent,
        });
      }
    } catch (error) {
      console.error("Error saving imported data to BOQ API:", error);
      message.error("Failed to save imported data. Please try again.");
      setIsContinuing(false);
      return;
    }
    setIsContinuing(false);

    navigateToFirstStep();
  };

  // Same job-type routing as Continue: the job type being imported wins, then
  // whichever job type the tender has selected (Roads & Sewers first).
  const navigateToFirstStep = () => {
    if (jobTypeParam === "groundworks") {
      navigate("/groundworks/cut-fills");
    } else if (jobTypeParam === "roadsAndSewers") {
      navigate("/roads-sewers/preliminary");
    } else if (state.jobTypes.roadsAndSewers) {
      navigate("/roads-sewers/preliminary");
    } else if (state.jobTypes.groundworks) {
      navigate("/groundworks/cut-fills");
    } else {
      navigate("/");
    }
  };

  const handleManualEntry = () => {
    // Both job types selected (and no specific take-off button was used) →
    // start at Groundworks, the first workflow step.
    if (!jobTypeParam && state.jobTypes.groundworks && state.jobTypes.roadsAndSewers) {
      navigate("/groundworks/cut-fills");
      return;
    }
    navigateToFirstStep();
  };

  return (
    <MainLayout canProceed showFooter={false}>
      <div className="max-w-4xl mx-auto">
        <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
          {/* Header */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <Title
              level={3}
              className="mb-1"
              style={{ fontWeight: 600, fontSize: "20px", color: "#111827" }}
            >
              Import Take-off Data
            </Title>
            <Text style={{ color: "#9ca3af", fontSize: "14px" }}>
              Import take-off data for the selected job type
              {selectedJobTypes.length > 1 ? "s" : ""}, or skip and enter it
              manually.
            </Text>
          </div>

          <div className="px-6 py-6">
            <Row gutter={[24, 24]}>
              {selectedJobTypes.map((jobType) => {
                const importPhase =
                  jobType.key === "groundworks"
                    ? groundworksImportPhase
                    : roadsAndSewersImportPhase;
                const isThisImporting = importPhase !== "idle";
                const importedSheets =
                  jobType.key === "groundworks"
                    ? importedGroundworksSheets
                    : importedRoadsAndSewersSheets;
                const handleImport =
                  jobType.key === "groundworks"
                    ? handleGroundworksImport
                    : handleRoadsAndSewersImport;
                const isThisDownloading =
                  jobType.key === "groundworks"
                    ? isDownloadingGroundworks
                    : isDownloadingRoadsAndSewers;
                const stepsProps = getImportStepsProps(
                  importPhase,
                  importedSheets !== null,
                );

                return (
                  <Col
                    xs={24}
                    md={selectedJobTypes.length > 1 ? 12 : 24}
                    key={jobType.key}
                  >
                    <Card
                      style={{
                        backgroundColor: jobType.accent,
                        borderRadius: "8px",
                        border:
                          importedSheets !== null
                            ? "1px solid #52c41a"
                            : undefined,
                      }}
                      bodyStyle={{ padding: "20px" }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {jobType.icon}
                          <Text style={{ fontWeight: 600, color: "#111827" }}>
                            {jobType.label}
                          </Text>
                        </div>
                        <div className="flex items-center gap-3">
                          {importedSheets !== null && (
                            <Text style={{ color: "#389e0d", fontSize: "13px", fontWeight: 500 }}>
                              <CheckCircleFilled style={{ marginRight: 4 }} />
                              Imported
                            </Text>
                          )}
                          {jobType.key === "roadsAndSewers" ? (
                            <Tooltip title="Roads & Sewers take-off sheet download is not available at the moment">
                              <span>
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<DownloadOutlined />}
                                  disabled
                                  style={{ padding: 0, height: "auto" }}
                                >
                                  Download Excel Template
                                </Button>
                              </span>
                            </Tooltip>
                          ) : (
                            <Button
                              type="link"
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={() => handleDownloadTemplate(jobType.key)}
                              loading={isThisDownloading}
                              style={{ padding: 0, height: "auto" }}
                            >
                              Download Excel Template
                            </Button>
                          )}
                        </div>
                      </div>
                      {stepsProps && (
                        <Steps
                          size="small"
                          items={IMPORT_STEP_ITEMS}
                          current={stepsProps.current}
                          status={stepsProps.status}
                          style={{ marginBottom: 16 }}
                        />
                      )}
                      <Upload.Dragger
                        accept=".xlsx,.xls"
                        disabled={isThisImporting}
                        showUploadList={false}
                        beforeUpload={handleImport}
                        style={{ backgroundColor: "#fff" }}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                          {importPhase === "validating"
                            ? "Validating take-off..."
                            : importPhase === "importing"
                              ? "Importing..."
                              : importPhase === "error"
                                ? "Validation failed"
                                : "Click or drag file to this area to import"}
                        </p>
                        <p className="ant-upload-hint">
                          {jobType.label} take-off Excel file (.xlsx, .xls)
                        </p>
                      </Upload.Dragger>
                      {importedSheets !== null && (
                        <Text
                          className="block mt-2"
                          style={{ color: "#389e0d", fontSize: "12px" }}
                        >
                          Successfully imported {importedSheets} worksheet
                          {importedSheets === 1 ? "" : "s"}. Importing again
                          will override this data.
                        </Text>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>

          {/* Footer Actions */}
          <div style={{ borderTop: "1px solid #e5e7eb" }}>
            {/* justify-content stays fixed (space-between) at all times so
                Continue never shifts position - Manual Entry keeps its
                layout slot (visibility: hidden, not unmounted/display:none)
                once data's been imported, instead of the button vanishing
                and Continue jumping to fill the gap. */}
            <div className="px-6 py-4 flex items-center justify-between">
              <Button
                size="large"
                onClick={handleManualEntry}
                disabled={isImporting || hasImportedAny || isContinuing}
                style={hasImportedAny ? { visibility: "hidden" } : undefined}
              >
                Manual Entry
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={handleContinue}
                // Enabled only once a file import has completed (Manual Entry is the
                // alternative path for skipping the import).
                disabled={isImporting || !hasImportedAny || isContinuing}
                loading={isContinuing}
                style={{
                  // Brand colour only while enabled — an inline background would
                  // override antd's grey disabled styling and leave a washed-out blue.
                  ...(isImporting || !hasImportedAny
                    ? {}
                    : { backgroundColor: COLORS.primary }),
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                Continue
                <ArrowRightOutlined />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default TenderImportTakeoff;
